/**
 * Util.js - Main Utility Class
 *
 * This file provides backward-compatible exports for the Util class.
 * The actual implementations have been extracted to domain-specific modules:
 *
 * - util/api/       - API communication (get, post, patch, del)
 * - util/auth/      - Authentication and token management
 * - util/cache/     - Report caching utilities
 * - util/format/    - Formatting utilities (dates, UUIDs, text)
 * - util/image/     - Image picking and uploading
 *
 * New code should import directly from the specific modules.
 * This class is maintained for backward compatibility.
 */

import { sendEmailNotification } from './lambdaUtil';

// Import from new modules
import { apiGet, apiPost, apiPatch, apiDel, printAPIError, handleApiError } from './api';
import {
  decodeJWT,
  getUserEmail,
  addHeaders,
  isTokenValid,
  getToken,
  storeToken,
} from './auth';
import { isReportCached, setReportCache } from './cache';
import {
  clearOrganizationAccess,
  setLoginMethod,
  getLoginMethod,
  getOrganization,
  setOrganization,
  setRouting,
  clearRouting,
  getFeatures,
  setFeatures,
  clearFeatures,
} from './storage';
import {
  calculateFontSize,
  formatUuid,
  formatDescription,
  textOnly,
  convertTo12HourTime,
  timeConverter,
} from './format';
import {
  imageMode,
  pickImage,
  blobToBase64,
  forceJPEGConversion,
  getImageClient,
  setUploadProgressURI,
  uploadImages,
  uploadImage,
  uploadSuccessful,
  anyUploadsFailed,
} from './image';
import { t } from '../i18n';

/**
 * Utility class providing static methods for common operations.
 * Maintained for backward compatibility - new code should import from specific modules.
 */
class Util {
  // ============================================
  // Formatting utilities (from util/format/)
  // ============================================
  static calculateFontSize = calculateFontSize;
  static formatUuid = formatUuid;
  static formatDescription = formatDescription;
  static textOnly = textOnly;
  static convertTo12HourTime = convertTo12HourTime;
  static timeConverter = timeConverter;

  // ============================================
  // API utilities (from util/api/)
  // ============================================
  static printAPIError = printAPIError;
  static handleApiError = handleApiError;

  static async get(apiName, path, options, noLogin = false) {
    return apiGet(apiName, path, options, noLogin);
  }

  static async post(apiName, path, options) {
    return apiPost(apiName, path, options);
  }

  static async patch(apiName, path, options) {
    return apiPatch(apiName, path, options);
  }

  static async del(apiName, path, options) {
    return apiDel(apiName, path, options);
  }

  // ============================================
  // Auth utilities (from util/auth/)
  // ============================================
  static decodeJWT = decodeJWT;
  static getUserEmail = getUserEmail;
  static addHeaders = addHeaders;
  static isTokenValid = isTokenValid;
  static getToken = getToken;
  static storeToken = storeToken;

  static async executeQRSignIn(organization, noLogin) {
    try {
      // Always clear organizationAccess when using QR sign-in
      // QR users have different permissions than regular Cognito users
      await clearOrganizationAccess();

      if (!(await isTokenValid())) {
        const response = await this.get('qrApi', '/qr-auth', {}, noLogin);
        const token = response['idToken'];
        await storeToken(token);
        await setLoginMethod('qr');
      }

      const current_org = await getOrganization();
      const login = await getLoginMethod();

      if (
        (current_org && current_org != organization) ||
        !current_org ||
        !login ||
        login !== 'qr'
      ) {
        await this.setOrganizationSettings(organization);
      }
    } catch (error) {
      let message = error.toString();
      if (!message.includes('Pending sign-in')) {
        alert(t('errors:auth.loginFailed'), error.message);
      }
    }
  }

  static async setOrganizationSettings(organization) {
    await clearRouting();
    await clearFeatures();
    await setOrganization(null);

    const request = { queryParams: { organization: organization } };
    const organizationData = await this.get('Resty', '/items/organization', request);

    const routing = organizationData['data']['routing'] ? organizationData['data']['routing'] : [];

    await setRouting(routing);

    // Load the per-org feature toggles so the UI/backend can gate optional features.
    // Failure to load features should never block login — default to no features enabled.
    try {
      const featureData = await this.get('Resty', '/items/organization/features', request);
      await setFeatures(Array.isArray(featureData?.items) ? featureData.items : []);
    } catch (error) {
      console.log('Could not load feature toggles:', error);
      await setFeatures([]);
    }

    await setOrganization(organization);
  }

  /**
   * Returns true when the given feature is enabled for the active organization.
   * Reads the cached feature list populated by setOrganizationSettings.
   * @param {string} featureKey
   * @returns {Promise<boolean>}
   */
  static async isFeatureEnabled(featureKey) {
    const features = await getFeatures();
    return Array.isArray(features) && features.includes(featureKey);
  }

  // ============================================
  // Cache utilities (from util/cache/)
  // ============================================
  static isReportCached = isReportCached;
  static setReportCache = setReportCache;

  // ============================================
  // Image utilities (from util/image/)
  // ============================================
  static imageMode = imageMode;
  static pickImage = pickImage;
  static blobToBase64 = blobToBase64;
  static forceJPEGConversion = forceJPEGConversion;
  static getImageClient = getImageClient;
  static setUploadProgressURI = setUploadProgressURI;
  static uploadImages = uploadImages;
  static uploadImage = uploadImage;
  static uploadSuccessful = uploadSuccessful;
  static anyUploadsFailed = anyUploadsFailed;

  /**
   * Get a signed URL for an uploaded image.
   *
   * @param {string} imageID - S3 key of the image
   * @param {boolean} extendedExpiration - Request the 7-day URL instead of 5 minutes.
   *   The server clamps this, so it can only ever shorten the lifetime, never extend it.
   * @param {string} [reportUUID] - UUID of the report referencing this image. The server
   *   uses it to verify the caller may see that report's images; pass it whenever the
   *   report is known. Omitting it is only correct at submission time, before the report
   *   exists.
   */
  static async getImageURL(imageID, extendedExpiration = false, reportUUID = null) {
    let expiration = extendedExpiration ? 7 * 24 * 60 * 60 : 60 * 5;
    try {
      return this.get('Resty', '/items/getImageURL', {
        queryParams: {
          ImageID: imageID,
          Expiration: expiration,
          ...(reportUUID ? { uuid: reportUUID } : {}),
        },
      })
        .then((result) => result)
        .catch((error) => {
          this.printAPIError(error);
        });
    } catch (e) {
      console.log(e.message);
    }
  }

  static async getUploadedImageURLs(
    tryUploadImage,
    uploadImagesArr,
    imagesToSend,
    setUploadedImages,
    setUploadStage,
    setUploadProgress,
  ) {
    let ui = uploadImagesArr;
    let fulfilledImageURLs;

    if (tryUploadImage == true) {
      ui = await this.uploadImages(imagesToSend, setUploadStage, setUploadProgress);
      setUploadedImages(() => ui);
      fulfilledImageURLs = ui
        .filter((value) => value['status'] == 'fulfilled')
        .map((value) => value['value']);
      if (fulfilledImageURLs.length < imagesToSend.length && !this.uploadSuccessful(ui)) {
        setUploadStage('Failed');
        return;
      }
    } else {
      fulfilledImageURLs = ui
        .filter((value) => value['status'] == 'fulfilled')
        .map((value) => value['value']);
    }

    let fullImageURLs = [];
    if (fulfilledImageURLs.length > 0) {
      const getImageURLPromises = fulfilledImageURLs.map((imageID) =>
        this.getImageURL(imageID, true),
      );
      fullImageURLs = await Promise.all(getImageURLPromises);
    }

    return [fullImageURLs, fulfilledImageURLs];
  }

  // ============================================
  // Email utilities (from util/lambdaUtil)
  // ============================================
  static async sendEmailNotification(
    details,
    message,
    type,
    organization,
    userId,
    images = [],
    isUrgent = false,
  ) {
    try {
      // Prepare email data using the shared utility function
      const emailData = await sendEmailNotification(
        details,
        message,
        type,
        organization,
        userId,
        images,
        isUrgent,
      );

      // Prepare request data
      const requestData = {
        body: emailData,
      };

      // Send email notification via Amplify API
      await this.post('Resty', '/items/emailNotification', requestData);
    } catch (e) {
      console.error('Error sending email notification:', e);
    }
  }
}

export default Util;

// Also export individual modules for direct imports
export * from './api';
export * from './auth';
export * from './cache';
export * from './format';
export * from './image';
